import React, { useEffect, useMemo, useState } from "react";
import { api, fmtINR, fmtDate, formatErr, today, resolveImage } from "@/lib/api";
import { PageHeader, Card, Empty } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2, Save, BookOpen, AlertTriangle, Bell, Phone, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useNavigate, useSearchParams } from "react-router-dom";

export default function NewOrder() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [rates, setRates] = useState({});
  const [customerId, setCustomerId] = useState("");
  const [orderDate, setOrderDate] = useState(today());
  const [items, setItems] = useState([{ product_id: "", quantity: "", rate: 0 }]);
  const [remarks, setRemarks] = useState("");
  const [saving, setSaving] = useState(false);

  // Customer Ledger states
  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [selectedCustomerObj, setSelectedCustomerObj] = useState(null);

  // Outstanding Warning Modal state
  const [outstandingModalOpen, setOutstandingModalOpen] = useState(false);
  const [modalCustomer, setModalCustomer] = useState(null);

  const [searchParams] = useSearchParams();
  const editId = searchParams.get("id");

  useEffect(() => {
    Promise.all([api.get("/products"), api.get("/customers")])
      .then(([p, c]) => { setProducts(p.data); setCustomers(c.data); });
  }, []);

  useEffect(() => {
    if (!customerId) {
      setLedgerEntries([]);
      setSelectedCustomerObj(null);
      return;
    }
    api.get("/rates", { params: { customer_id: customerId } }).then(r => {
      const m = {};
      for (const x of r.data) m[x.product_id] = x.selling_price;
      setRates(m);
      setItems(prev => prev.map(it => it.product_id ? { ...it, rate: m[it.product_id] ?? 0 } : it));
    });

    // Fetch ledger & selected customer details
    api.get("/ledger", { params: { customer_id: customerId } }).then(r => setLedgerEntries(r.data));
    const matchedCust = customers.find(c => c.id === customerId);
    if (matchedCust) {
      setSelectedCustomerObj(matchedCust);
      if ((matchedCust.outstanding || 0) > 0) {
        setModalCustomer(matchedCust);
        setOutstandingModalOpen(true);
      }
    } else {
      api.get("/customers").then(r => {
        const found = r.data.find(c => c.id === customerId);
        if (found) {
          setSelectedCustomerObj(found);
          if ((found.outstanding || 0) > 0) {
            setModalCustomer(found);
            setOutstandingModalOpen(true);
          }
        }
      });
    }
  }, [customerId, customers]);

  useEffect(() => {
    if (!editId) return;

    api.get(`/orders/${editId}`)
      .then((res) => {
        const order = res.data;

        setCustomerId(order.customer_id);
        setOrderDate(order.order_date);
        setRemarks(order.remarks || "");

        setItems(
          order.items.map(item => ({
            product_id: item.product_id,
            quantity: item.quantity,
            rate: item.rate,
          }))
        );
      });
  }, [editId]);

  function setItem(idx, patch) {
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, ...patch } : it));
  }
  function addRow() { setItems([...items, { product_id: "", quantity: "", rate: 0 }]); }
  function removeRow(idx) { setItems(items.filter((_, i) => i !== idx)); }

  function onPickProduct(idx, pid) {
    setItem(idx, { product_id: pid, rate: rates[pid] ?? 0 });
  }

  const total = useMemo(() => items.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.rate) || 0), 0), [items]);

  async function submit() {
    if (!customerId)
      return toast.error("Select customer");

    const valid = items.filter(
      i => i.product_id && Number(i.quantity) > 0
    );

    if (valid.length === 0)
      return toast.error("Add at least one item");

    setSaving(true);

    try {
      const payload = {
        customer_id: customerId,
        order_date: orderDate,
        remarks,
        items: valid.map(i => ({
          product_id: i.product_id,
          quantity: Number(i.quantity),
          rate: Number(i.rate)
        }))
      };

      let res;
      if (editId) {
        res = await api.put(`/orders/${editId}`, payload);
        toast.success("Order Updated");
      } else {
        res = await api.post("/orders", payload);
        toast.success("Order Created");
      }

      navigate(`/orders/${res.data.id}`);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div data-testid="new-order-page" className="space-y-4">
      <PageHeader
        eyebrow="Sales Entry"
        title={editId ? "Edit Order" : "Create Order"}
        description="Pick a customer, add line items — rates auto-fill from the customer's pricing matrix."
        actions={
          <Button onClick={submit} disabled={saving} className="bg-cobalt-500 hover:bg-cobalt-700 text-white" data-testid="order-submit-btn">
            {saving ? "Saving..." : editId ? "Update Order" : "Create Order"}
          </Button>
        }
      />

      <Card padding="p-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label>Customer</Label>
            <Select value={customerId} onValueChange={setCustomerId}>
              <SelectTrigger data-testid="order-customer"><SelectValue placeholder="Select customer"/></SelectTrigger>
              <SelectContent>
                {customers.map(c => <SelectItem key={c.id} value={c.id}>{c.business_name} — {c.business_type}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Order Date</Label>
            <Input type="date" value={orderDate} onChange={e=>setOrderDate(e.target.value)} data-testid="order-date"/>
          </div>
          <div>
            <Label>Remarks</Label>
            <Input value={remarks} onChange={e=>setRemarks(e.target.value)} placeholder="optional"/>
          </div>
        </div>
      </Card>

      <Card padding="p-0">
        <div className="overflow-x-auto">
          <table className="ddms-table" data-testid="order-items-table">
            <thead><tr><th>Product</th><th>Unit</th><th className="num" style={{width: 140}}>Qty</th><th className="num" style={{width: 140}}>Rate (₹)</th><th className="num">Amount</th><th></th></tr></thead>
            <tbody>
              {items.map((it, idx) => {
                const prod = products.find(p => p.id === it.product_id);
                const amt = (Number(it.quantity) || 0) * (Number(it.rate) || 0);
                return (
                  <tr key={idx}>
                    <td style={{ minWidth: 260 }}>
                      <div className="flex items-center gap-2">
                        {prod?.image_url ? (
                          <img src={resolveImage(prod.image_url)} alt={prod.product_name} className="w-9 h-9 rounded-md object-cover border border-slate-200 shrink-0"/>
                        ) : (
                          <div className="w-9 h-9 rounded-md border border-dashed border-slate-300 bg-slate-50 shrink-0"/>
                        )}
                        <Select value={it.product_id} onValueChange={(v)=>onPickProduct(idx, v)}>
                          <SelectTrigger><SelectValue placeholder="Select product"/></SelectTrigger>
                          <SelectContent>
                            {products.map(p => <SelectItem key={p.id} value={p.id}>{p.product_name} (/{p.unit})</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </td>
                    <td className="text-slate-500">{prod?.unit || "—"}</td>
                    <td><Input type="number" step="0.01" value={it.quantity} onChange={e=>setItem(idx,{quantity: e.target.value})} className="text-right font-mono" data-testid={`qty-${idx}`}/></td>
                    <td><Input type="number" step="0.01" value={it.rate} onChange={e=>setItem(idx,{rate: e.target.value})} className="text-right font-mono"/></td>
                    <td className="num font-semibold">{fmtINR(amt)}</td>
                    <td className="num"><Button size="sm" variant="outline" onClick={()=>removeRow(idx)} className="text-rose-600 border-rose-200"><Trash2 size={12}/></Button></td>
                  </tr>
                );
              })}
              <tr>
                <td colSpan={6}><Button variant="outline" size="sm" onClick={addRow} data-testid="add-item-btn"><Plus size={12} className="mr-1"/>Add row</Button></td>
              </tr>
              <tr className="bg-cobalt-50/60">
                <td colSpan={4} className="num font-semibold uppercase tracking-wider text-xs">Total</td>
                <td className="num font-bold text-cobalt-700 text-base">{fmtINR(total)}</td>
                <td/>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>

      {/* Customer Ledger Section */}
      {customerId && (
        <Card padding="p-5" className="mt-6 border border-slate-200">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4 pb-3 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <BookOpen size={18} className="text-cobalt-600" />
              <div>
                <h3 className="font-heading font-semibold text-slate-900 text-base">
                  Customer Ledger Statement — {selectedCustomerObj?.business_name || "Selected Customer"}
                </h3>
                <p className="text-xs text-slate-500">
                  Recent transactions, debit/credit entries, and current running balance.
                </p>
              </div>
            </div>

            {selectedCustomerObj && (
              <div className="flex items-center gap-4 text-xs">
                <div className="bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-200">
                  <span className="text-slate-500 uppercase tracking-wider text-[10px] block font-semibold">Outstanding</span>
                  <span className="font-mono font-bold text-rose-600 text-sm">{fmtINR(selectedCustomerObj.outstanding || 0)}</span>
                </div>
                <div className="bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-200">
                  <span className="text-slate-500 uppercase tracking-wider text-[10px] block font-semibold">Advance</span>
                  <span className="font-mono font-bold text-emerald-600 text-sm">{fmtINR(selectedCustomerObj.advance || 0)}</span>
                </div>
              </div>
            )}
          </div>

          {ledgerEntries.length === 0 ? (
            <div className="py-6"><Empty title="No ledger entries for this customer yet" /></div>
          ) : (
            <div className="overflow-x-auto border border-slate-200 rounded-lg">
              <table className="ddms-table text-xs">
                <thead>
                  <tr className="bg-slate-50">
                    <th>Date</th>
                    <th>Type</th>
                    <th>Reference</th>
                    <th>Description</th>
                    <th className="num">Debit (₹)</th>
                    <th className="num">Credit (₹)</th>
                    <th className="num">Balance (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  {[...ledgerEntries]
                    .sort((a, b) => new Date(b.transaction_date).getTime() - new Date(a.transaction_date).getTime() || new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime())
                    .map((e) => (
                    <tr key={e.id}>
                      <td className="font-mono text-xs">{fmtDate(e.transaction_date)}</td>
                      <td>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                          e.transaction_type === "Order"
                            ? "bg-amber-50 text-amber-700"
                            : "bg-emerald-50 text-emerald-700"
                        }`}>
                          {e.transaction_type}
                        </span>
                      </td>
                      <td className="font-mono text-xs font-medium text-slate-700">{e.reference_no || "—"}</td>
                      <td className="text-slate-600">{e.remarks || "—"}</td>
                      <td className="num font-mono text-rose-600 font-semibold">{e.debit ? fmtINR(e.debit) : "—"}</td>
                      <td className="num font-mono text-emerald-600 font-semibold">{e.credit ? fmtINR(e.credit) : "—"}</td>
                      <td className="num font-mono font-bold text-slate-800">{fmtINR(-e.balance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* Outstanding Balance Warning Modal */}
      <Dialog open={outstandingModalOpen} onOpenChange={setOutstandingModalOpen}>
        <DialogContent className="sm:max-w-md p-0 overflow-hidden border-0 shadow-2xl rounded-2xl">
          <div className="bg-gradient-to-r from-rose-600 to-amber-600 p-5 text-white">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-white text-lg font-heading font-semibold">
                <div className="p-2 bg-white/10 backdrop-blur-md rounded-xl">
                  <AlertTriangle size={20} className="text-amber-200" />
                </div>
                Outstanding Balance Notice
              </DialogTitle>
            </DialogHeader>
            <div className="mt-2 text-xs text-rose-100 opacity-90">
              Customer has a pending dues balance. Please notify customer or request payment before extending further credit.
            </div>
          </div>

          <div className="p-5 space-y-4 bg-slate-50/50">
            {/* Customer Details Box */}
            <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
              <div>
                <div className="text-xs text-slate-400 font-semibold uppercase tracking-wider">Customer</div>
                <div className="font-heading font-bold text-slate-900 text-base mt-0.5">{modalCustomer?.business_name}</div>
                <div className="text-xs text-slate-500">{modalCustomer?.customer_name} ({modalCustomer?.business_type})</div>
              </div>
              <div className="text-right">
                <span className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-rose-50 text-rose-700 border border-rose-200">
                  {modalCustomer?.payment_cycle || "Monthly"} Billing
                </span>
              </div>
            </div>

            {/* Financial Status Grid */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-rose-50/80 p-3.5 rounded-xl border border-rose-200">
                <div className="text-[11px] font-semibold text-rose-700 uppercase tracking-wider">Total Dues Due</div>
                <div className="font-mono text-xl font-bold text-rose-700 mt-1">{fmtINR(modalCustomer?.outstanding || 0)}</div>
              </div>

              <div className="bg-slate-100 p-3.5 rounded-xl border border-slate-200">
                <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Credit Limit</div>
                <div className="font-mono text-xl font-bold text-slate-800 mt-1">
                  {modalCustomer?.credit_limit ? fmtINR(modalCustomer.credit_limit) : "No Limit"}
                </div>
              </div>
            </div>

            {/* Contact Details */}
            <div className="bg-white p-3 rounded-xl border border-slate-200 space-y-2 text-xs">
              <div className="flex items-center justify-between text-slate-600">
                <span className="text-slate-400">Primary Mobile (Owner):</span>
                <span className="font-mono font-medium text-slate-800">{modalCustomer?.mobile || "N/A"}</span>
              </div>
              {modalCustomer?.mobile_secondary && (
                <div className="flex items-center justify-between text-slate-600 pt-1.5 border-t border-slate-100">
                  <span className="text-slate-400">Secondary Mobile (Cashier):</span>
                  <span className="font-mono font-medium text-slate-800">{modalCustomer.mobile_secondary}</span>
                </div>
              )}
            </div>
          </div>

          <div className="px-5 py-3.5 bg-slate-100 border-t border-slate-200 flex items-center justify-between gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const phoneRaw = (modalCustomer?.mobile || "").replace(/\D/g, "");
                const phone = phoneRaw.length === 10 ? `91${phoneRaw}` : phoneRaw;
                const text = encodeURIComponent(`Hello ${modalCustomer?.customer_name},\nThis is a gentle reminder from Omkar Milk Distributor. Your current outstanding balance is ${fmtINR(modalCustomer?.outstanding || 0)}. Kindly clear the payment at your earliest convenience.\nThank you!`);
                window.open(phone ? `https://wa.me/${phone}?text=${text}` : `https://wa.me/?text=${text}`, "_blank", "noopener,noreferrer");
              }}
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-xs font-semibold"
            >
              <Bell size={13} className="mr-1 text-emerald-600" /> Notify via WhatsApp
            </Button>
            <Button
              onClick={() => setOutstandingModalOpen(false)}
              className="bg-slate-900 hover:bg-slate-800 text-white text-xs font-medium px-4"
            >
              <CheckCircle2 size={13} className="mr-1" /> Continue Order
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
